(function () {
  if (!window.Matter) return;

  const hero = document.querySelector("[data-hero-physics]");
  if (!hero) return;

  const canvas = hero.querySelector("[data-hero-physics-canvas]");
  if (!canvas) return;

  const { Engine, Bodies, Composite, Body, Vector, Events } = Matter;

  /* =========================
     CONFIG
  ========================= */
  const COLOR_DARK = "#242424";
  const COLOR_LIGHT = "#D8D1C1";

  const MAX_BLOBS = 120;
  const INITIAL_BLOBS = 24;
  const SPAWN_INTERVAL = 600;
  const FLOOR_THICKNESS = 3;

  // Cursor push (no click)
  const CURSOR_RADIUS = 160;
  const CURSOR_FORCE = 0.00006; // strength of push
  const CURSOR_MAX_FORCE = 0.0025; // safety cap

  // Tide
  const TIDE_STRENGTH = 0.000003;
  const TIDE_SPEED = 0.00035;

  // Squish (visual only)
  const MAX_SQUISH_FLOOR = 0.03;
  const MAX_SQUISH_BLOB = 0.015;
  const SQUISH_SPRING = 0.16;
  const SQUISH_DAMPING = 0.80;

  // Spawn animation
  const SPAWN_SCALE_START = 0.2;
  const SPAWN_SPRING = 0.19;
  const SPAWN_DAMPING = 0.82;

  /* =========================
     SIZE TIERS
  ========================= */
  const SIZE_TIERS = [
    { r: 120, m: 8.5, chance: 0.02, liquidity: 0.05, fallBoost: 1.35 },
    { r: 64, m: 6, chance: 0.06, liquidity: 0.4, fallBoost: 1.15 },
    { r: 52, m: 4.8, chance: 0.14, liquidity: 0.6, fallBoost: 1.0 },
    { r: 40, m: 3.5, chance: 0.22, liquidity: 0.75, fallBoost: 0.95 },
    { r: 30, m: 2.3, chance: 0.26, liquidity: 0.9, fallBoost: 0.9 },
    { r: 22, m: 1.6, chance: 0.30, liquidity: 1.0, fallBoost: 0.85 }
  ];

  /* =========================
     ENGINE
  ========================= */
  const engine = Engine.create();
  engine.gravity.y = 1.25;
  engine.positionIterations = 14;
  engine.velocityIterations = 12;

  const world = engine.world;

  /* =========================
     STATE
  ========================= */
  let blobs = [];
  let floor;
  let width = 0;
  let height = 0;
  let lastSpawn = 0;

  const ctx = canvas.getContext("2d");

  // Mouse in viewport coords, converted each frame to hero-local coords
  const mouse = { x: -9999, y: -9999, inHero: false };
  window.addEventListener("mousemove", (e) => {
    mouse.x = e.clientX;
    mouse.y = e.clientY;
  });

  /* =========================
     HELPERS
  ========================= */
  const pickTier = () => {
    const r = Math.random();
    let acc = 0;
    for (const tier of SIZE_TIERS) {
      acc += tier.chance;
      if (r <= acc) return tier;
    }
    return SIZE_TIERS[SIZE_TIERS.length - 1];
  };

  const pickColor = () => (Math.random() < 0.1 ? COLOR_LIGHT : COLOR_DARK);

  /* =========================
     CREATE BLOB
  ========================= */
  const createBlob = (initial = false) => {
    const tier = pickTier();

    const body = Bodies.circle(
      Math.random() * width,
      initial ? height - tier.r - Math.random() * 80 : tier.r + 10,
      tier.r,
      {
        mass: tier.m,
        restitution: 0.55,
        friction: 0.75,
        frictionAir: 0.12 + tier.m * 0.004
      }
    );

    if (!initial) {
      Body.setVelocity(body, {
        x: (Math.random() - 0.5) * 1.1,
        y: (1.05 + tier.m * 0.28) * tier.fallBoost
      });
    }

    const blob = {
      body,
      radius: tier.r,
      color: pickColor(),
      sizeTier: tier,
      squish: 0,
      squishVelocity: 0,
      spawnScale: initial ? 1 : SPAWN_SCALE_START,
      spawnVelocity: 0,
      tideOffset: Math.random() * Math.PI * 2
    };

    Composite.add(world, body);
    blobs.push(blob);

    if (blobs.length > MAX_BLOBS) {
      const oldest = blobs.shift();
      Composite.remove(world, oldest.body);
    }
  };

  /* =========================
     WORLD
  ========================= */
  const buildWorld = () => {
    const rect = hero.getBoundingClientRect();
    width = Math.max(1, rect.width);
    height = Math.max(1, rect.height);

    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    Composite.clear(world, false);
    blobs = [];

    floor = Bodies.rectangle(
      width / 2,
      height + FLOOR_THICKNESS,
      width * 2,
      FLOOR_THICKNESS,
      { isStatic: true, restitution: 0.6 }
    );

    Composite.add(world, floor);

    for (let i = 0; i < INITIAL_BLOBS; i++) createBlob(true);
  };

  /* =========================
     COLLISIONS → SQUISH
  ========================= */
  Events.on(engine, "collisionStart", (e) => {
    e.pairs.forEach(({ bodyA, bodyB }) => {
      for (const blob of blobs) {
        const liquidity = blob.sizeTier.liquidity;

        if (
          (bodyA === blob.body && bodyB === floor) ||
          (bodyB === blob.body && bodyA === floor)
        ) {
          blob.squishVelocity -= MAX_SQUISH_FLOOR * liquidity;
        } else {
          // blob–blob collisions
          for (const other of blobs) {
            if (other === blob) continue;
            if (
              (bodyA === blob.body && bodyB === other.body) ||
              (bodyB === blob.body && bodyA === other.body)
            ) {
              blob.squishVelocity -= MAX_SQUISH_BLOB * liquidity;
              break;
            }
          }
        }
      }
    });
  });

  /* =========================
     UPDATE + DRAW
  ========================= */
  const update = (time) => {
    Engine.update(engine, 1000 / 60);

    // Spawn loop
    if (time - lastSpawn > SPAWN_INTERVAL) {
      createBlob(false);
      lastSpawn = time;
    }

    // Convert mouse viewport coords to hero-local coords
    const heroRect = hero.getBoundingClientRect();
    const mx = mouse.x - heroRect.left;
    const my = mouse.y - heroRect.top;
    mouse.inHero = mx >= 0 && my >= 0 && mx <= heroRect.width && my <= heroRect.height;

    ctx.clearRect(0, 0, width, height);

    for (const blob of blobs) {
      // Squish spring
      blob.squishVelocity += -blob.squish * SQUISH_SPRING;
      blob.squishVelocity *= SQUISH_DAMPING;
      blob.squish += blob.squishVelocity;

      // Spawn growth
      blob.spawnVelocity += (1 - blob.spawnScale) * SPAWN_SPRING;
      blob.spawnVelocity *= SPAWN_DAMPING;
      blob.spawnScale += blob.spawnVelocity;

      // Tide
      const wave =
        Math.sin(time * TIDE_SPEED + blob.tideOffset) *
        TIDE_STRENGTH /
        blob.body.mass;

      Body.applyForce(blob.body, blob.body.position, { x: wave, y: 0 });

      // Cursor push (no click) – only when cursor is inside hero
      if (mouse.inHero) {
        const dx = blob.body.position.x - mx;
        const dy = blob.body.position.y - my;
        const dist = Math.hypot(dx, dy);

        if (dist < CURSOR_RADIUS && dist > 0.0001) {
          const falloff = 1 - dist / CURSOR_RADIUS;
          const force = Math.min((CURSOR_FORCE * falloff) / blob.body.mass, CURSOR_MAX_FORCE);
          const dir = Vector.normalise({ x: dx, y: dy });

          Body.applyForce(blob.body, blob.body.position, {
            x: dir.x * force,
            y: dir.y * force
          });
        }
      }

      // Draw (visual squish only)
      const { x, y } = blob.body.position;
      const sx = (1 + blob.squish) * blob.spawnScale;
      const sy = (1 - blob.squish) * blob.spawnScale;

      ctx.save();
      ctx.translate(x, y);
      ctx.scale(sx, sy);
      ctx.beginPath();
      ctx.arc(0, 0, blob.radius, 0, Math.PI * 2);
      ctx.fillStyle = blob.color;
      ctx.fill();
      ctx.restore();
    }

    requestAnimationFrame(update);
  };

  window.addEventListener("resize", buildWorld);

  /* =========================
     INIT
  ========================= */
  buildWorld();
  requestAnimationFrame(update);
})();
